import { usePrefetchedSuspenseQuery } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMorphemeMnemonicsPanelMorphemeUserSliceQueryOptions } from "@-/dev.improve.haniverse.lib.morpheme.read";
import { useResolvedQuery } from "@-/dev.improve.haniverse.lib.query-options";
import { MorphemePanelsFlashcardProviders } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels";
import {
  DrillPageContext,
  GraphemeDrillContext,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { type ReactNode, useCallback, useRef } from "react";

/**
 * The providers a GRAPHEME review card's back and Info need on top of the drill's own contexts:
 * the grapheme's mnemonics user slice and the panels contexts the mnemonics and Info surfaces read.
 * Mounted inside the grapheme branch only, so the readonly slice is the one the drill's gate
 * already narrowed.
 *
 * Read through `usePrefetchedSuspenseQuery` so a prepared card never suspends here, and a
 * favorite or study write rotating the user slice refetches in place rather than re-suspending.
 */
export function DrillFlashcardProvidersSuspense({ children }: { children: ReactNode }) {
  const [character, morphemeHrefObject] = useContextSelectors(
    DrillPageContext,
    (c) => c.character,
    (c) => c.morphemeHrefObject,
  );
  const [mnemonicsSlice] = useContextSelectors(GraphemeDrillContext, (c) => c.slice);
  const mnemonicsUserSliceQueryOptions =
    useMorphemeMnemonicsPanelMorphemeUserSliceQueryOptions(character);
  const { data: mnemonicsUserSlice } = usePrefetchedSuspenseQuery(mnemonicsUserSliceQueryOptions);
  const resolvedMnemonicsUserSlice = useResolvedQuery(
    mnemonicsUserSliceQueryOptions.queryKey,
    mnemonicsUserSlice,
  );

  // Inert nav plumbing: nothing on a review card steps the mnemonics pronunciation tabs from
  // outside (that is the lesson action area's job), so the panels context gets no-op navigation.
  const viewNavigationRef = useRef(null);
  const noopSetActiveIndex = useCallback(() => {}, []);

  return (
    <MorphemePanelsFlashcardProviders
      activeIndex={0}
      character={character}
      // A review's introduced readings are the ones already in the learner's deck.
      introducedPinyinList={mnemonicsUserSlice.enrolledPinyinList}
      mnemonicsSlice={mnemonicsSlice}
      mnemonicsUserSlice={resolvedMnemonicsUserSlice}
      morphemeHrefObject={morphemeHrefObject}
      setActiveIndex={noopSetActiveIndex}
      viewNavigationRef={viewNavigationRef}
    >
      {children}
    </MorphemePanelsFlashcardProviders>
  );
}
